import React, { useEffect, useRef, useState } from 'react';

/**
 * LazyMount — renders an empty placeholder until the user scrolls
 * within `rootMargin` of it, then mounts `children`. Once mounted, it
 * stays mounted.
 *
 * Purpose: keep below-the-fold heavy sections (trainer grid, member-app
 * mockups, memberships pricing, testimonials marquee, contact form,
 * bottom video) out of the initial render tree on older iPhones / iPads.
 * Before this, every <Home> mount cost the device the parse + image
 * decode + effect-mount of 8+ heavy sections at first paint — even
 * sections the user might never scroll to. This drops first-paint cost
 * dramatically on low-memory devices.
 *
 * Behaviour:
 *   • Uses IntersectionObserver with a generous rootMargin (default
 *     400px) so the section is fully mounted, hydrated, and image-
 *     decoded by the time it actually enters the viewport — the user
 *     never sees a pop-in flash.
 *   • Placeholder reserves vertical space via `minHeight` (default
 *     `60vh`) to avoid scroll-position jumps as sections mount.
 *   • Falls back to immediate mount if IntersectionObserver is
 *     unavailable (very old browsers) — never blocks content.
 *   • If `eager` prop is true, skips lazy mounting entirely (useful
 *     when a parent already knows the section is critical).
 *
 * Safe for SSR: window/IntersectionObserver references are only read
 * inside useEffect, never during render.
 */
export default function LazyMount({
  children,
  rootMargin = '400px',
  minHeight = '60vh',
  eager = false,
}) {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(eager);

  useEffect(() => {
    if (mounted) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setMounted(true);
            io.disconnect();
          }
        });
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mounted, rootMargin]);

  if (mounted) return children;
  return <div ref={ref} aria-hidden style={{ minHeight }} />;
}
